import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { useTranslations } from "use-intl";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { LanguageSwitch } from "@/components/app/language-switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import { qk, useConfig } from "@/lib/queries";

function Shell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col px-5 pb-10 pt-safe">
      <div className="flex items-center justify-between pt-4">
        <Link to="/" className="flex items-center gap-2 font-bold">
          <img src="/icon.svg" alt="" className="size-8 rounded-lg" />
          LiftQuest
        </Link>
        <LanguageSwitch />
      </div>
      <div className="mt-14">
        <h1 className="text-3xl font-black tracking-tight">{title}</h1>
        <p className="mt-2 text-muted-foreground">{subtitle}</p>
      </div>
      {children}
    </main>
  );
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {children}
    </p>
  );
}

/** Step 1: ask for the email address; the server mails a link (if the account exists). */
export function ForgotPasswordPage() {
  const t = useTranslations("auth");
  const tc = useTranslations("common");
  const config = useConfig();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  if (config.data?.passwordReset === false) return <Navigate to="/login" replace />;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = String(new FormData(e.currentTarget).get("email")).trim();
    setError(null);
    setPending(true);
    const res = await authClient.requestPasswordReset({ email });
    setPending(false);
    if (res.error) return setError(res.error.status === 429 ? t("tooMany") : tc("error"));
    setSentTo(email);
  }

  return (
    <Shell title={t("forgotTitle")} subtitle={t("forgotSubtitle")}>
      {sentTo ? (
        <div className="mt-8 flex gap-3 rounded-2xl border bg-card p-4" role="status">
          <MailCheck className="mt-0.5 size-6 shrink-0 text-primary" />
          <p className="text-sm">{t("linkSent", { email: sentTo })}</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="email">{t("email")}</Label>
            <Input id="email" name="email" type="email" required autoComplete="email" inputMode="email" className="h-12 text-base" />
          </div>
          {error && <ErrorText>{error}</ErrorText>}
          <Button type="submit" size="lg" className="mt-2 h-12 rounded-2xl text-base" disabled={pending}>
            {pending && <Loader2 className="animate-spin" />}
            {t("sendLink")}
          </Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="font-semibold text-primary">
          {t("backToLogin")}
        </Link>
      </p>
    </Shell>
  );
}

/** Step 2: the link from the mail (…/reset-password?token=…) – choose a new password. */
export function ResetPasswordPage() {
  const t = useTranslations("auth");
  const tc = useTranslations("common");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(!token || params.get("error") === "INVALID_TOKEN");
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const password = String(form.get("password"));
    setError(null);
    if (password.length < 8) return setError(t("weakPassword"));
    if (password !== String(form.get("confirm"))) return setError(t("passwordsDiffer"));
    setPending(true);
    const res = await authClient.resetPassword({ newPassword: password, token: token! });
    setPending(false);
    if (res.error) {
      if (res.error.code === "INVALID_TOKEN") return setInvalid(true);
      return setError(res.error.status === 429 ? t("tooMany") : tc("error"));
    }
    // Every session of the account was revoked, this browser's included.
    qc.removeQueries({ queryKey: qk.me });
    toast.success(t("resetDone"));
    navigate("/login", { replace: true });
  }

  return (
    <Shell title={t("resetTitle")} subtitle={t("resetSubtitle")}>
      {invalid ? (
        <div className="mt-8 space-y-4">
          <ErrorText>{t("resetInvalid")}</ErrorText>
          <Button asChild size="lg" className="h-12 w-full rounded-2xl text-base">
            <Link to="/forgot-password">{t("requestNewLink")}</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="password">{t("newPassword")}</Label>
            <Input id="password" name="password" type="password" required minLength={8} autoComplete="new-password" className="h-12 text-base" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="confirm">{t("confirmPassword")}</Label>
            <Input id="confirm" name="confirm" type="password" required minLength={8} autoComplete="new-password" className="h-12 text-base" />
          </div>
          {error && <ErrorText>{error}</ErrorText>}
          <Button type="submit" size="lg" className="mt-2 h-12 rounded-2xl text-base" disabled={pending}>
            {pending && <Loader2 className="animate-spin" />}
            {t("savePassword")}
          </Button>
        </form>
      )}
    </Shell>
  );
}
